import { $window } from '@umu/umu-dom';
import { checkIsAuthCDNUrl } from '@umu/umu-util';
import { useMemoizedFn } from 'ahooks';
import { type RefObject, useCallback, useEffect, useRef } from 'react';
import { AnnotationsLayer } from '../services/annotationsLayer';
import { ImageLoadQueue } from '../services/imageLoadQueue';
import type { IPdfImages } from '../types';
import { RENDER_TYPE } from '../types';
import { getPixelRatio } from '../utils/common';
import { getViewport } from '../utils/pdf';
import { getPdfPerfConfig, perfEnd, perfStart } from '../utils/perfMetrics';

export interface UseImageRendererProps {
    pageNum: number;
    scale: number;
    renderPixelRadio?: number;
    canvasRef: RefObject<HTMLCanvasElement>;
    annotationLayerRef?: RefObject<HTMLDivElement>;
    pdfImages?: IPdfImages;
    onRenderSuccess?: () => void;
    // biome-ignore lint/suspicious/noExplicitAny: error type
    onRenderError?: (error: any) => void;
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js pdfDoc
    pdfDoc?: any;
    onInternalLinkClick?: (pageNum: number) => void;
}

/**
 * Image 渲染 Hook
 *
 * 从 usePdfRenderer 拆分：封装 Image 渲染路径，包含 ImageLoadQueue 生命周期管理与预加载逻辑。
 */
export function useImageRenderer(props: UseImageRendererProps) {
    const {
        pageNum,
        scale,
        renderPixelRadio,
        canvasRef,
        annotationLayerRef,
        pdfImages,
        onRenderSuccess,
        onRenderError,
        pdfDoc,
        onInternalLinkClick,
    } = props;

    const imageLoadQueueRef = useRef<ImageLoadQueue | null>(null);
    const lastPageNumRef = useRef<number>(0);
    const imagePreloadConfigRef = useRef<{ pre: number; next: number }>({ pre: 2, next: 3 });
    /** Image 渲染失败后禁用（避免每次都卡住），回退到 Canvas 渲染 */
    const isImageRenderDisabledRef = useRef(false);

    // pdfImages 变化时重建队列
    useEffect(() => {
        if (pdfImages && pdfImages.list.length > 0) {
            const cfg = getPdfPerfConfig();
            imageLoadQueueRef.current = new ImageLoadQueue(pdfImages, {
                maxCacheSize: cfg.imageCacheSize,
                maxParallelDownloads: cfg.imageParallel,
            });
            imagePreloadConfigRef.current = {
                pre: cfg.imagePreCount && cfg.imagePreCount > 0 ? cfg.imagePreCount : 2,
                next: cfg.imageNextCount && cfg.imageNextCount > 0 ? cfg.imageNextCount : 3,
            };
        } else {
            imageLoadQueueRef.current = null;
        }
    }, [pdfImages]);

    // 页码变化时预热当前页（不重建队列，保留缓存）
    useEffect(() => {
        if (!(pdfImages && imageLoadQueueRef.current)) return;
        if (pageNum > 0) {
            imageLoadQueueRef.current.preload(pageNum - 1);
        }
    }, [pageNum, pdfImages]);

    /** 从队列或直接加载图片元素 */
    const loadImageElement = useMemoizedFn(async (imageIdx: number, imageUrl: string): Promise<HTMLImageElement> => {
        if (imageLoadQueueRef.current) {
            return imageLoadQueueRef.current.load(imageIdx);
        }
        return new Promise<HTMLImageElement>((resolve, reject) => {
            const tmp = new Image();
            tmp.crossOrigin = checkIsAuthCDNUrl(imageUrl) ? 'use-credentials' : 'anonymous';
            tmp.onload = () => resolve(tmp);
            tmp.onerror = () => reject(new Error(`image load failed: ${imageUrl}`));
            tmp.src = imageUrl;
        });
    });

    /** 绘制完成后挂载注解层并触发预加载 */
    const finalizeRender = useMemoizedFn(
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js types
        (page: any, viewport: any, canvas: HTMLCanvasElement, scaleFactor: number) => {
            if (annotationLayerRef?.current) {
                new AnnotationsLayer(
                    $window.pdfjsLib,
                    page,
                    viewport,
                    canvas,
                    annotationLayerRef.current,
                    scaleFactor,
                    pdfDoc,
                    onInternalLinkClick
                );
            }
            if (imageLoadQueueRef.current) {
                const dir = lastPageNumRef.current < pageNum ? 'next' : 'pre';
                const cfg = imagePreloadConfigRef.current;
                imageLoadQueueRef.current.preloadAround(pageNum - 1, dir, cfg.pre, cfg.next);
            }
            lastPageNumRef.current = pageNum;
            onRenderSuccess?.();
        }
    );

    const renderByImage = useCallback(
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
        async (page: any, isStale?: () => boolean) => {
            if (!pdfImages) {
                const error = new Error('pdfImages is null');
                onRenderError?.(error);
                throw error;
            }
            if (pageNum > pdfImages.list.length) {
                const error = new Error(
                    `pageNum out of range: pageNum=${pageNum}, listLength=${pdfImages.list.length}`
                );
                onRenderError?.(error);
                throw error;
            }
            if (!pdfImages.list[pageNum - 1]) {
                const error = new Error(`image not found: pageNum=${pageNum}`);
                onRenderError?.(error);
                throw error;
            }

            if (!canvasRef.current) return;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            if (!context) return;
            if (isStale?.()) return;

            try {
                const pixelRatio = renderPixelRadio || getPixelRatio();
                const scaleFactorStr = getComputedStyle(canvas).getPropertyValue('--scale-factor');
                const scaleFactor = scaleFactorStr ? Number(scaleFactorStr) : scale;

                const viewport = getViewport(page, scaleFactor * pixelRatio);

                const cssWidth = `calc(var(--scale-factor) * ${Math.floor(
                    viewport.width / (pixelRatio * scaleFactor)
                )}px)`;
                const cssHeight = `calc(var(--scale-factor) * ${Math.floor(
                    viewport.height / (pixelRatio * scaleFactor)
                )}px)`;

                const imageKey = pdfImages.list[pageNum - 1];
                const imageUrl = `${pdfImages.path}${imageKey}`;

                perfStart('image_load', String(pageNum), { renderType: RENDER_TYPE.IMAGE });
                let img: HTMLImageElement;
                try {
                    img = await loadImageElement(pageNum - 1, imageUrl);
                } finally {
                    perfEnd('image_load', String(pageNum), { renderType: RENDER_TYPE.IMAGE });
                }

                if (isStale?.()) return;

                canvas.width = Math.floor(viewport.width);
                canvas.height = Math.floor(viewport.height);
                canvas.style.width = cssWidth;
                canvas.style.height = cssHeight;

                perfStart('image_draw', String(pageNum), { renderType: RENDER_TYPE.IMAGE });
                context.clearRect(0, 0, canvas.width, canvas.height);
                context.drawImage(img, 0, 0, viewport.width, viewport.height);
                perfEnd('image_draw', String(pageNum), { renderType: RENDER_TYPE.IMAGE });

                if (!isStale?.()) {
                    finalizeRender(page, viewport, canvas, scaleFactor);
                }
            } catch (err: unknown) {
                onRenderError?.(err);
                throw err;
            }
        },
        [
            pdfImages,
            pageNum,
            scale,
            canvasRef,
            annotationLayerRef,
            onRenderSuccess,
            onRenderError,
            renderPixelRadio,
            pdfDoc,
            onInternalLinkClick,
        ]
    );

    return { renderByImage, isImageRenderDisabledRef };
}
